import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

// Heart-button favorites are now stored as items in the user's default
// "My favorites" collection. The trigger `handle_new_user_collection`
// guarantees a default exists for every user, and the migration
// backfilled existing rows from the old `favorites` table — so this
// hook keeps the same Set<string> API while persisting to the new schema.
async function getDefaultCollectionId(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from("collections")
    .select("id")
    .eq("user_id", userId)
    .eq("is_default", true)
    .maybeSingle();
  if (error || !data) return null;
  return data.id;
}

export const useFavorites = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [defaultId, setDefaultId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setFavorites(new Set());
      setDefaultId(null);
      return;
    }
    setLoading(true);
    (async () => {
      const did = await getDefaultCollectionId(user.id);
      setDefaultId(did);
      if (!did) {
        setLoading(false);
        return;
      }
      const { data, error } = await supabase
        .from("collection_items")
        .select("item_ref")
        .eq("collection_id", did)
        .eq("item_kind", "tool");
      if (!error && data) {
        setFavorites(new Set(data.map((d) => d.item_ref)));
      }
      setLoading(false);
    })();
  }, [user]);

  const toggleFavorite = useCallback(
    async (slug: string) => {
      if (!user) {
        toast.error("Sign in to bookmark tools");
        return;
      }
      let did = defaultId;
      if (!did) {
        did = await getDefaultCollectionId(user.id);
        setDefaultId(did);
      }
      if (!did) {
        toast.error("Couldn't find your default collection");
        return;
      }
      const isFav = favorites.has(slug);
      // Optimistic update
      setFavorites((prev) => {
        const next = new Set(prev);
        isFav ? next.delete(slug) : next.add(slug);
        return next;
      });

      if (isFav) {
        const { error } = await supabase
          .from("collection_items")
          .delete()
          .eq("collection_id", did)
          .eq("item_kind", "tool")
          .eq("item_ref", slug);
        if (error) {
          setFavorites((prev) => new Set(prev).add(slug));
          toast.error("Failed to remove bookmark");
        }
      } else {
        const { error } = await supabase
          .from("collection_items")
          .insert({ collection_id: did, item_kind: "tool", item_ref: slug, added_by: user.id });
        if (error) {
          setFavorites((prev) => {
            const next = new Set(prev);
            next.delete(slug);
            return next;
          });
          if (!error.message.toLowerCase().includes("duplicate")) {
            toast.error("Failed to bookmark");
          }
        } else {
          toast.success("Bookmarked!");
        }
      }
    },
    [user, favorites, defaultId]
  );

  const isFavorite = useCallback((slug: string) => favorites.has(slug), [favorites]);

  return { favorites, toggleFavorite, isFavorite, loading };
};
